import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithReauth from '@/pages/shared/base-query-auth';
import { runMutationOnce, runTableQuery, stripEmptyParams } from '@/pages/shared/rtk-query-helpers';

const postTo = (url) => (model) => ({ url, method: 'POST', body: model });

export const approvalSummaryApi = createApi({
    reducerPath: 'approvalSummaryApi',
    baseQuery: baseQueryWithReauth,
    endpoints: (builder) => ({
        // Mutations throughout, including the lookup lists: they are POST searches driven
        // by the caller's filter model and must never be served from cache.
        getTableData: builder.mutation({ query: postTo('/api/approvalSummary/list') }),
        getAuthorList: builder.mutation({ query: postTo('/api/approvalSummary/approvalAuthors') }),
        getSitesList: builder.mutation({ query: postTo('/api/approvalSummary/sites') }),
        getStatusesList: builder.mutation({ query: postTo('/api/approvalSummary/statuses') }),
        getApproversList: builder.mutation({ query: postTo('/api/approvalSummary/approvers') }),
        approveDocuments: builder.mutation({ query: postTo('/api/approvalSummary/approve') }),
        rejectDocument: builder.mutation({ query: postTo('/api/approvalSummary/reject') }),
        rollbackRevision: builder.mutation({ query: postTo('/api/approvalSummary/rollback') }),
        putDocumentsOnHold: builder.mutation({
            query: ({ ids, comment }) => ({
                url: '/api/approvalSummary/hold',
                method: 'POST',
                body: { ids, comment },
            }),
        }),
        getEligibleApprovalCheckers: builder.mutation({
            query: postTo('/api/approvalSummary/eligibleCheckers'),
        }),
        assignApprovalCheckers: builder.mutation({
            query: postTo('/api/approvalSummary/assignCheckers'),
        }),
        reassignRevision: builder.mutation({
            query: ({ documentFileIds, approverUserId, siteId }) => ({
                url: `/api/approvalSummary/reassign/${approverUserId}`,
                method: 'POST',
                params: stripEmptyParams({ siteId }),
                body: documentFileIds,
            }),
        }),
    }),
});

export const {
    usePutDocumentsOnHoldMutation,
    useGetEligibleApprovalCheckersMutation,
    useAssignApprovalCheckersMutation,
} = approvalSummaryApi;

/**
 * Runs an `approvalSummaryApi` endpoint once and clears its tracked entry.
 */
export const runApprovalSummaryMutation = (dispatch, endpointName, arg) =>
    runMutationOnce(dispatch, approvalSummaryApi.endpoints[endpointName], arg);

/**
 * Loads the approval summary table, wrapped in the `{ data }` envelope that the shared
 * `getTableData` thunk still expects from a table `request` function.
 */
export const runApprovalSummaryTableQuery = (dispatch, requestData) =>
    runTableQuery(dispatch, approvalSummaryApi.endpoints.getTableData, requestData);

export default approvalSummaryApi;
